// Shared source of truth for tourism experience enums.
// Consumed by both the frontend (ES module import) and backend (CommonJS require).
//
// These lists used to live in three places — route validation in
// server/routes/tourismExperiencesRoutes.js, the audit in
// scripts/tourism_health_check.js, and the label map in
// src/services/tourismExperiencesService.js — and they had drifted apart:
// route validation rejected `walking` (a quarter of the corpus) and
// `out_and_back`, while the label map still carried categories no row had
// ever used. Adding a value in one place and not the others is how that
// happened, so there is now only one place.
//
// A category names the LOAD-BEARING segment of an experience: remove a
// segment and if the experience survives without it, that segment was
// convenience, not identity. The rules and the reasoning behind each
// category live in docs/TOURISM_EXPERIENCE_SPEC.md.

// Keys are the stored enum values; values are the default English labels.
// Labels are not i18n-migrated yet — when they are, the catalog keys should
// match these object keys so the mapping stays mechanical.
const EXPERIENCE_CATEGORIES = {
  // Transport-led — the vehicle is the load-bearing segment, and the payoff
  // is wherever it puts you down.
  scenic_rail: 'Scenic Rail',
  heritage_railway: 'Heritage Railway',
  iconic_tram: 'Iconic Tram',
  funicular: 'Funicular',
  cable_car: 'Cable Car',
  gondola: 'Gondola',
  chairlift: 'Chairlift',
  boat_cruise: 'Boat Cruise',
  river_cruise: 'River Cruise',
  ferry_route: 'Ferry Route',
  scenic_drive: 'Scenic Drive',
  scenic_flight: 'Scenic Flight',
  helicopter_tour: 'Helicopter Tour',
  hot_air_balloon: 'Hot Air Balloon',
  // Two or more load-bearing conveyances. Not "a walk that has a boat" —
  // remove any leg of a genuine multi_transport and it is a different trip.
  multi_transport: 'Multi-Transport',

  // Activity-led — the walking (or the site) is the load-bearing segment.
  day_hike: 'Day Hike',
  multi_day_trek: 'Multi-Day Trek',
  walking_tour: 'Walking Tour',
  // A route the traveller rides under their own power. Admitted only where a
  // NATIONAL OR STATE DESIGNATION PROGRAMME publishes the named route (Oregon
  // State Scenic Bikeways, EuroVelo, national numbered networks) — a mapped
  // bike path with no designating authority is not a product, and the
  // 7-day exertion cap in criterion 9 bounds the long-distance tail.
  cycling_route: 'Cycling Route',
  // Bounded + ticketed + prescribed route inside a managed site. The planning
  // problem (last entry, capacity, shuttle timetable) is what separates this
  // from an open hillside walk, not the fact of being inside a park.
  park_circuit: 'Park Circuit',
  underground_tour: 'Underground',
  cave_tour: 'Cave Tour',
  zip_line_course: 'Zip Line Course',
  // A ride whose payoff is a named view rather than a place you walk from.
  observation_experience: 'Observation',

  // Recurring city-organised markets.
  christmas_market: 'Christmas Market',
  street_market: 'Street Market',
};

const TRANSPORT_MODES = {
  rail: 'Rail',
  cable_car: 'Cable Car',
  gondola: 'Gondola',
  chairlift: 'Chairlift',
  funicular: 'Funicular',
  // A Ferris/observation wheel is its own conveyance. Before migration 258
  // these rows carried `cable_car`, which rendered a cable-car icon on a
  // Ferris wheel.
  observation_wheel: 'Observation Wheel',
  boat: 'Boat',
  ferry: 'Ferry',
  car: 'Car',
  bus: 'Bus',
  walking: 'Walking',
  // The second self-powered mode. Spec criterion 9's 7-day exertion cap has
  // named `cycling` since 2026-09-09 and tourism_health_check.js has always
  // carried it in SELF_POWERED; the enum simply never caught up.
  cycling: 'Cycling',
  balloon: 'Balloon',
  helicopter: 'Helicopter',
  zip_line: 'Zip Line',
  mixed: 'Mixed',
};

// How a transport mode renders. Kept here so the emoji, the Icon name and the
// label cannot drift apart across the three surfaces that draw them (the
// service's chips, the feed card, and normalizeCardData's badge). `label`
// falls back to TRANSPORT_MODES; the aliases at the end are legacy values that
// still appear in older rows and other content types.
const TRANSPORT_PRESENTATION = {
  rail: { emoji: '🚂', icon: 'train' },
  cable_car: { emoji: '🚠', icon: 'cableCar' },
  gondola: { emoji: '🚡', icon: 'cableCar' },
  chairlift: { emoji: '🚡', icon: 'cableCar' },
  funicular: { emoji: '🚞', icon: 'train' },
  // `eye` rather than a vehicle glyph: Icon.js has no wheel, and drawing a
  // cable car next to the London Eye is the exact contradiction migration 258
  // exists to remove. "Observation" is the honest read of what the ride is for.
  observation_wheel: { emoji: '🎡', icon: 'eye' },
  boat: { emoji: '⛵', icon: 'boat' },
  ferry: { emoji: '⛴️', icon: 'boat' },
  car: { emoji: '🚗', icon: 'car', label: 'Drive' },
  bus: { emoji: '🚌', icon: 'bus' },
  walking: { emoji: '🚶', icon: 'walking' },
  cycling: { emoji: '🚲', icon: 'bicycle' },
  balloon: { emoji: '🎈', icon: 'sun' },
  helicopter: { emoji: '🚁', icon: 'sun' },
  zip_line: { emoji: '🛫', icon: 'loopArrow' },
  mixed: { emoji: '🔄', icon: 'loopArrow', label: 'Multi-Transport' },
  // Legacy aliases — not valid transport_mode values, but present in old rows.
  train: { emoji: '🚂', icon: 'train', label: 'Rail' },
  boating: { emoji: '⛵', icon: 'boat', label: 'Boat' },
  driving: { emoji: '🚗', icon: 'car', label: 'Drive' },
};

// Categories that render a hiking boot rather than their transport emoji.
// `park_circuit` belongs here: a ticketed boardwalk circuit is walked.
const FOOT_CATEGORIES = ['day_hike', 'multi_day_trek', 'park_circuit'];

// Which scenario chips a category can satisfy, consumed by
// contentScoringService when it derives content_scores.scenario_tags. This
// lived as three literal arrays in that file and was missed when categories
// were added, so rows silently fell out of the Outdoor/Adventure/Cultural
// feeds. Categories absent from every list intentionally carry no scenario.
const SCENARIO_CATEGORY_TAGS = {
  outdoor: [
    'scenic_rail', 'gondola', 'chairlift', 'scenic_drive', 'boat_cruise',
    'river_cruise', 'ferry_route', 'cable_car', 'funicular', 'day_hike',
    'multi_day_trek', 'park_circuit', 'observation_experience', 'iconic_tram',
    'scenic_flight', 'hot_air_balloon', 'zip_line_course', 'helicopter_tour',
    'cycling_route',
  ],
  adventure: [
    'gondola', 'chairlift', 'cable_car', 'funicular', 'scenic_flight',
    'zip_line_course', 'helicopter_tour', 'hot_air_balloon', 'multi_day_trek',
    'underground_tour', 'cave_tour',
  ],
  cultural: [
    'heritage_railway', 'iconic_tram', 'walking_tour', 'underground_tour',
    'cave_tour', 'christmas_market', 'street_market', 'observation_experience',
  ],
};

const ROUTE_TYPES = {
  one_way: 'One Way',
  round_trip: 'Round Trip',
  loop: 'Loop',
  point_to_point: 'Point to Point',
  out_and_back: 'Out and Back',
};

const STOP_TYPES = {
  boarding: 'Boarding',
  alighting: 'Alighting',
  transfer: 'Transfer',
  viewpoint: 'Viewpoint',
  break: 'Break',
  visit: 'Visit',
  photo_stop: 'Photo Stop',
  optional_stop: 'Optional Stop',
  waypoint: 'Waypoint',
};

// What a stored price is charged FOR (migration 273). NOT a CHECK constraint on
// the column, for the reason migration 245 recorded: SQLite cannot alter a CHECK
// without a table rebuild, and adding `per_cabin` later must not need a
// migration. Enforced on write instead, like ROUTE_TYPES and STOP_TYPES.
//
// NULL is a valid stored value and means "unstated" — readers render NULL as
// per-person, which is the pre-273 behaviour. Backfilling every legacy row to
// `per_person` would assert exactly what is false for the NPS road-toll rows.
const PRICE_UNITS = {
  per_person: 'per person',
  per_vehicle: 'per vehicle',
  per_group: 'per group',
};

const DIFFICULTY_LEVELS = ['easy', 'moderate', 'challenging'];

// `cave_tour` is retained above only so a row that predates migration 258 on
// an un-migrated volume still renders a label instead of its raw enum string.
// Nothing should be seeded into it — use `underground_tour`.
const DEPRECATED_EXPERIENCE_CATEGORIES = ['cave_tour'];

const SEEDABLE_EXPERIENCE_CATEGORIES = Object.keys(EXPERIENCE_CATEGORIES)
  .filter((k) => !DEPRECATED_EXPERIENCE_CATEGORIES.includes(k));

module.exports = {
  PRICE_UNITS,
  VALID_PRICE_UNITS: Object.keys(PRICE_UNITS),
  EXPERIENCE_CATEGORIES,
  TRANSPORT_MODES,
  TRANSPORT_PRESENTATION,
  FOOT_CATEGORIES,
  SCENARIO_CATEGORY_TAGS,
  ROUTE_TYPES,
  STOP_TYPES,
  DIFFICULTY_LEVELS,
  DEPRECATED_EXPERIENCE_CATEGORIES,
  SEEDABLE_EXPERIENCE_CATEGORIES,
  VALID_EXPERIENCE_CATEGORIES: Object.keys(EXPERIENCE_CATEGORIES),
  VALID_TRANSPORT_MODES: Object.keys(TRANSPORT_MODES),
  VALID_ROUTE_TYPES: Object.keys(ROUTE_TYPES),
  VALID_STOP_TYPES: Object.keys(STOP_TYPES),
};
